/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import useClickOutside from "../../../hook/useClickOutside";
import {
  IContactEmailStatus,
  IContactEmailType,
} from "../../../types/EmailTypes";
import IconBerserkerMail from "../../Icon";
import StyledButton from "../../StyledButton";
import { DateFilter, EMAIL_TYPE_NAME, FilterEmail } from "./Emails";

const Popover = styled.div`
  position: absolute;
  z-index: 999;
  height: fit-content;
  width: 200px;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  border: 1px solid;
  border-bottom: none;
  border-top: none;
  display: flex;
  flex-direction: column;
  padding: 10px 0px;
  right: 170px;
  top: 10px;
  background-color: #fff;
`;

interface ButtonFilterProps {
  selected: boolean;
}

const StyledButtonWithIcon = styled(StyledButton)<ButtonFilterProps>`
  margin-right: 15px;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  position: relative;

  ${(props) =>
    props.selected &&
    css`
      border-color: #fb972e;
      padding-right: 12px;
    `}

  > div {
    margin-right: 7px;
  }

  .close {
    margin-left: 12px;
    margin-right: 0px;
    padding: 2px;
  }

  :hover {
    opacity: 1 !important;
  }
`;

const TitlePopover = styled.div`
  font-size: 12px;
  color: #888888;
  padding: 10px;
  margin-left: 7px;
  font-weight: 400;
`;

const AddContactPopover = styled.div`
  position: absolute;
  background: white;
  z-index: 20;
  top: 42px;
  height: fit-content;
  max-height: 200px;
  width: 180px;
  border-radius: 5px;
  box-shadow: 0px 3px 20px #00000026;
  border: 1px solid #d4d4d4;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 0px;
  right: -10px;
`;

const GroupName = styled.div`
  display: flex;
  align-items: center;
  color: #888888;
  font-weight: 400;
  margin-left: 7px;
  padding: 10px;
  font-size: 12px;
`;

const Number = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: #fb972e;
  color: #fff;
  width: 20px;
  height: 20px;
  font-size: 12px;
  border-radius: 100%;
  font-weight: 600;
  line-height: 20px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 15px;
  color: #2f2f2f;
  cursor: pointer;
  position: relative;
  width: 100%;
  justify-content: space-between;
  font-weight: 400;

  span {
    margin-left: 7px;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
  }
`;

const categoriCss = css`
  font-weight: 400;
  :hover {
    svg {
      path {
        fill: #ffffff;
        stroke: #ffffff;
      }
    }
  }
`;

interface Props {
  filter: FilterEmail;
  handleChangeFilter: (vle: FilterEmail) => void;
}

export default function FilterEmails(props: Props) {
  const { filter, handleChangeFilter } = props;
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const [openDropView, setOpenDropView] = useState(false);

  const [showStatus, setShowStatus] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const [showType, setShowType] = useState(false);

  function selectStatus(s: IContactEmailStatus) {
    if (filter.status.find((status) => status === s)) {
      const newStatus = filter.status.filter((cc) => cc !== s);
      handleChangeFilter({ ...filter, status: newStatus });
    } else {
      handleChangeFilter({ ...filter, status: [...filter.status, s] });
    }
  }

  function selectType(t: IContactEmailType) {
    if (filter.type.find((type) => type === t)) {
      const newTypes = filter.type.filter((tt) => tt !== t);
      handleChangeFilter({ ...filter, type: newTypes });
    } else {
      handleChangeFilter({ ...filter, type: [...filter.type, t] });
    }
  }

  return (
    <StyledButtonWithIcon
      selected={filter.status.length > 0 || filter.type.length > 0}
      inverted
      css={css`
        margin-right: 0;
      `}
      onClick={(e) => {
        setOpenDropView(true);
        e.stopPropagation();
      }}
    >
      {filter.status.length > 0 || filter.type.length > 0 ? (
        <Number>{filter.status.length + filter.type.length}</Number>
      ) : (
        <IconBerserkerMail name="filter" />
      )}
      Filter
      {(filter.status.length > 0 || filter.type.length > 0) && (
        <div
          className="close"
          onClick={(e) => {
            handleChangeFilter({ ...filter, status: [], type: [] });
            setOpenDropView(false);
            e.stopPropagation();
          }}
        >
          <IconBerserkerMail
            name="close-modal"
            color="#bbb"
            width={12}
            height={12}
          />
        </div>
      )}
      {openDropView && (
        <AddContactPopover ref={wrapperRef}>
          <TitlePopover>FILTERS</TitlePopover>

          <Row
            onMouseOver={() => setShowDate(true)}
            onMouseLeave={() => setShowDate(false)}
          >
            <span>By Date</span>

            {showDate && (
              <Popover>
                <GroupName>DATE</GroupName>

                <Row
                  css={categoriCss}
                  onClick={() => {
                    handleChangeFilter({ ...filter, date: DateFilter.Recent });
                    setOpenDropView(false);
                  }}
                >
                  <span>Most Recent</span>
                  {filter.date === DateFilter.Recent && (
                    <IconBerserkerMail
                      name="check"
                      color="#fb972e"
                      width={12}
                      height={10}
                    />
                  )}
                </Row>
                <Row
                  css={categoriCss}
                  onClick={() => {
                    handleChangeFilter({ ...filter, date: DateFilter.Older });
                    setOpenDropView(false);
                  }}
                >
                  <span>Older</span>

                  {filter.date === DateFilter.Older && (
                    <IconBerserkerMail
                      name="check"
                      color="#fb972e"
                      width={12}
                      height={10}
                    />
                  )}
                </Row>
              </Popover>
            )}
          </Row>

          <Row
            onMouseOver={() => setShowType(true)}
            onMouseLeave={() => setShowType(false)}
          >
            <span>By Type</span>

            {showType && (
              <Popover>
                <GroupName>TYPE</GroupName>

                {[IContactEmailType.BROADCAST, IContactEmailType.CAMPAIGN].map(
                  (t) => {
                    return (
                      <Row css={categoriCss} onClick={() => selectType(t)}>
                        <span>{EMAIL_TYPE_NAME[t]}</span>

                        {filter.type.find((typ) => typ === t) && (
                          <IconBerserkerMail
                            name="check"
                            color="#fb972e"
                            width={12}
                            height={10}
                          />
                        )}
                      </Row>
                    );
                  }
                )}
              </Popover>
            )}
          </Row>

          <Row
            onMouseOver={() => setShowStatus(true)}
            onMouseLeave={() => setShowStatus(false)}
          >
            <span>By Status</span>

            {showStatus && (
              <Popover>
                <GroupName>STATUS</GroupName>

                {[IContactEmailStatus.OPENED, IContactEmailStatus.UNOPENED].map(
                  (t) => {
                    return (
                      <Row css={categoriCss} onClick={() => selectStatus(t)}>
                        <span>
                          {t === IContactEmailStatus.OPENED
                            ? "Opened"
                            : "Unopened"}
                        </span>

                        {filter.status.find((typ) => typ === t) && (
                          <IconBerserkerMail
                            name="check"
                            color="#fb972e"
                            width={12}
                            height={10}
                          />
                        )}
                      </Row>
                    );
                  }
                )}
              </Popover>
            )}
          </Row>
        </AddContactPopover>
      )}
    </StyledButtonWithIcon>
  );
}
